import { useState } from "react";
import { useTranslation } from "react-i18next";
import useAmenityCatalog from "@/hooks/useAmenityCatalog";
import Amenity from "@/model/Amenity";
import "@/components/AmenitiesSelect/AmenitiesSelect.scss";

interface AmenitiesSelectProps {
  value: Amenity[];
  onChange: (amenities: Amenity[]) => void;
}

/**
 * The ~160 amenities of the catalog as checkboxes, in collapsible categories showing how many are
 * picked, with a search box that filters by name (and opens every category with a match).
 */
function AmenitiesSelect({ value, onChange }: AmenitiesSelectProps) {
  const { t } = useTranslation();
  const { categories, isLoading, error } = useAmenityCatalog();
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();
  const visibleCategories = categories
    .map((category) => ({
      ...category,
      amenities: query
        ? category.amenities.filter((amenity) => amenity.label.toLowerCase().includes(query))
        : category.amenities,
    }))
    .filter((category) => category.amenities.length > 0);

  const toggle = (code: Amenity) =>
    onChange(value.includes(code) ? value.filter((item) => item !== code) : [...value, code]);

  if (isLoading) {
    return <p className="amenities-select__help">{t("amenities.loading")}</p>;
  }
  if (error) {
    return <p className="amenities-select__error">{t("amenities.loadError")}</p>;
  }

  return (
    <div className="amenities-select">
      <input
        type="search"
        className="amenities-select__search"
        placeholder={t("amenities.searchPlaceholder")}
        aria-label={t("amenities.searchPlaceholder")}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <p className="amenities-select__help">
        {t("amenities.selectedCount", { count: value.length })}
      </p>

      {visibleCategories.length === 0 && (
        <p className="amenities-select__help">{t("amenities.noMatch")}</p>
      )}

      {visibleCategories.map((category) => {
        const selected = category.amenities.filter((amenity) => value.includes(amenity.code)).length;
        return (
          // Keyed by query too, so a search re-opens the categories it matches.
          <details
            key={`${category.code}-${query === "" ? "all" : "search"}`}
            className="amenities-select__category"
            open={query !== "" || undefined}
          >
            <summary>
              {category.label}
              <span className="amenities-select__count">
                {selected}/{category.amenities.length}
              </span>
            </summary>
            <div className="amenities-select__options">
              {category.amenities.map((amenity) => (
                <label key={amenity.code} className="amenities-select__option">
                  <input
                    type="checkbox"
                    checked={value.includes(amenity.code)}
                    onChange={() => toggle(amenity.code)}
                  />
                  <span>
                    {amenity.label}
                    {amenity.description && <small>{amenity.description}</small>}
                  </span>
                </label>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

export default AmenitiesSelect;
export type { AmenitiesSelectProps };
